import { MenuIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Drawer } from "vaul";

import { OpenAPI } from "@/client";
import { MainNavigation } from "@/components/main-navigation";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { isFessh } from "@/utils/is-fessh";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import { LoadingScreen } from "../loading-screen";
import { Logo } from "../logo";
import { Button } from "../ui/button";

function FullWidthLayout() {
  const { t } = useTranslation();
  const { congress } = useCongress();
  const { getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set favicon based on isFessh
    const favicon =
      (document.querySelector("link[rel*='icon']") as HTMLLinkElement) ||
      document.createElement("link");
    favicon.type = "image/x-icon";
    favicon.rel = "shortcut icon";
    favicon.href = isFessh ? "/fessh-favicon.ico" : "/cw-favicon.ico";
    document.getElementsByTagName("head")[0].appendChild(favicon);
  }, []);

  useEffect(() => {
    const getUserMetadata = async () => {
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: `https://${import.meta.env.VITE_AUTH0_DOMAIN}/api/v2/`,
            // scope: "read:current_user update:current_user_metadata"
          },
        });
        if (accessToken) {
          OpenAPI.TOKEN = accessToken;
          setIsLoading(false);
        }
      } catch (e) {
        if (e instanceof Error) {
          console.log(e.message);
        }
      }
    };

    getUserMetadata();
  }, [getAccessTokenSilently]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <div className="fixed inset-y-0 z-50 hidden flex-col md:flex">
        <MainNavigation />
      </div>
      <div className="sticky top-0 flex h-16 shrink-0 items-center gap-x-4 border-b border-border bg-card px-4 shadow-sm sm:gap-x-6 sm:px-6 md:hidden lg:px-8">
        <div className="ml-auto w-full">
          <Drawer.Root direction="left">
            <div className="flex">
              <div className="mr-auto flex shrink-0 items-center">
                <Logo />
              </div>
              <Drawer.Trigger asChild>
                <Button variant="ghost" size="icon">
                  <MenuIcon aria-hidden="true" />
                </Button>
              </Drawer.Trigger>
            </div>
            <Drawer.Portal>
              <Drawer.Overlay className="fixed inset-0 bg-foreground/40 dark:bg-background/40" />
              <Drawer.Content className="fixed inset-y-0 left-0 z-10 flex outline-none">
                <Drawer.Title className="hidden">{t("drawer.menu")}</Drawer.Title>
                <Drawer.Description className="hidden">
                  {t("drawer.primaryNavigation")}
                </Drawer.Description>
                <div className="my-2 ml-2 flex w-[310px] grow flex-col overflow-hidden rounded-[16px] bg-card p-5">
                  <MainNavigation />
                </div>
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer.Root>
        </div>
      </div>
      <div className="md:pl-72">
        <main className="mx-auto max-w-screen-lg px-3 py-10">
          {congress && <Outlet />}
        </main>
      </div>
    </>
  );
}

export { FullWidthLayout };
